import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useDataProvider, useGetList, useNotify } from "react-admin";
import { sendSentryException } from "../api/SentryErrorHandler";
import { Download, GetApp } from "@mui/icons-material";

export const ErrorReportForm = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [username, setUsername] = useState("");
  const [clientkey, setClientkey] = useState("");
  const [requestList, setRequestList] = useState(false);
  const [unsortedListData, setData] = useState([]);
  const [reportuuid, setReportuuid] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortedField, setSortedField] = useState("report_time");
  const [noReports, setNoReports] = useState(false);

  const { data, error, isLoading } = useGetList(
    "issue_report",
    { id: clientkey, meta: { username, client_key: clientkey } },
    { enabled: requestList }
  );

  useEffect(() => {
    if (requestList && !isLoading) {
      if (error) {
        if (error.status == 404) {
          setData([]);
          alert("Invalid Client Key or Username");
        }
      } else if (data && data.length > 0) {
        setData(data);
        setNoReports(false);
      } else {
        setData([]);
        setNoReports(true);
      }
      setRequestList(false);
    }
  }, [requestList, data, error, isLoading]);

  const downloadLogFile = async (reportUrl, uuid) => {
    try {
      const { data: downloadData } = await dataProvider.getOne("issue_report_download", { url: reportUrl });
      const text = JSON.stringify(downloadData);
      const jsonData = JSON.parse(text);

      // Extract application logs from JSON data
      let { application_logs } = jsonData;
      // Remove the application_logs from jsonData so it doesn't get repeated
      delete jsonData.application_logs;

      const formattedJson = JSON.stringify(jsonData, null, 2);

      // Add new lines before and after "--------- beginning of main"
      application_logs = application_logs
        .replace("--------- beginning of main", "--------- beginning of main\n")
        .replace("--------- beginning of system", "\n--------- beginning of system\n");

      // Replace \n with actual newlines in the logs
      const formattedLogs = application_logs.replace(/\\n/g, "\n");

      // Combine the formatted JSON and the formatted logs
      const combinedFormattedText = `${formattedJson}\n\n${formattedLogs}`;
      const blob = new Blob([combinedFormattedText], { type: "text/plain" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${clientkey}-${uuid}-${username}.log`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      /*
                The data provider will catch an error and report to sentry.
                However, it won't catch or send any errors after getting the required data.
                So, we will send any edge case errors to Sentry ourselves. 
            */
      sendSentryException(error, "downloadLogFile");
    }
  };

  const downloadGPXFile = async (reportUrl, uuid) => {
    try {
      const { data: downloadData } = await dataProvider.getOne("issue_report_download", { url: reportUrl });
      const text = JSON.stringify(downloadData);
      const jsonData = JSON.parse(text);

      let { application_logs } = jsonData;
      // Replace \n with actual newlines in the logs
      const formattedLogs = application_logs.replace(/\\n/g, "\n");
      // Extract ELEOS_GPX events from the logs
      const gpxLines = formattedLogs.split("\n").filter((line) => line.includes("ELEOS_GPX:"));
      if (gpxLines.length === 0) {
        notify("No ELEOS_GPX location data found in this report", { type: "error" });
        return;
      }

      // Parse the GPX data
      const locations = gpxLines
        .map((line) => {
          // Extract the CSV part after "ELEOS_GPX:"
          const match = line.match(/ELEOS_GPX:\s*(.+)$/);
          if (match) {
            try {
              const csvData = match[1].trim();
              const [time, latitude, longitude, heading, speed, altitude, accuracy] = csvData.split(",");
              return { time, latitude, longitude, heading, speed, altitude, accuracy };
            } catch (e) {
              return null;
            }
          }
          return null;
        })
        .filter((loc) => loc !== null);

      if (locations.length === 0) {
        notify("Could not parse ELEOS_GPX location data", { type: "error" });
        return;
      }
      const gpxContent = convertToGPX(locations);
      // Download the GPX file
      const blob = new Blob([gpxContent], { type: "application/gpx+xml" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${clientkey}-${uuid}-${username}.gpx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      sendSentryException(error, "downloadGPXFile");
      notify(`Error generating GPX file: ${error}`, { type: "error" });
    }
  };

  // Essentially taken from: https://github.com/eleostech/mobile-reference-iphone/blob/master/tools/convert-to-gpx.sh (which is shared between iOS and android)
  const convertToGPX = (locations) => {
    let gpx = '<?xml version="1.0" encoding="UTF-8"?>\n';
    gpx += '<gpx xmlns="http://www.topografix.com/GPX/1/1" version="1.1">\n';
    gpx += "<trk>\n";
    gpx += "<trkseg>\n";

    locations.forEach((loc) => {
      const { time, latitude, longitude, heading, speed, altitude, accuracy } = loc;
      gpx += `  <trkpt lat="${latitude}" lon="${longitude}">\n`;
      gpx += `    <time>${time}</time>\n`;
      gpx += `    <ele>${altitude}</ele>\n`;
      gpx += `    <speed>${speed}</speed>\n`;
      gpx += `    <hdop>${accuracy}</hdop>\n`;
      gpx += `    <extensions>\n`;
      gpx += `      <heading>${heading}</heading>\n`;
      gpx += `    </extensions>\n`;
      gpx += `  </trkpt>\n`;
      gpx += "\n";
    });

    gpx += "</trkseg>\n";
    gpx += "</trk>\n";
    gpx += "</gpx>";

    return gpx;
  };

  const submitHandler = (event) => {
    event.preventDefault();
    if (!clientkey || !username) {
      alert("Both Client Key and Username are required");
      return;
    }
    setRequestList(true);
  };

  const onDownload = async (uuid, type) => {
    setReportuuid(uuid);
    const { data } = await dataProvider.getOne("issue_report", { id: uuid });
    const reportData = data;
    if (reportData) {
      type === "issue-report" ? downloadLogFile(reportData.url, uuid) : downloadGPXFile(reportData.url, uuid);
    }
  };

  const changeSortOrder = (field) => {
    const isAsc = sortedField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortedField(field);
  };

  const sortedListData = [...unsortedListData].sort((a, b) => {
    return sortDirection === "asc"
      ? new Date(a[sortedField]) - new Date(b[sortedField])
      : new Date(b[sortedField]) - new Date(a[sortedField]);
  });

  return (
    <div>
      <h2>Error Report Tool</h2>
      <p>Here you can enter a client key and a Username, and receive a list of related error reports</p>
      <Container>
        <form onSubmit={submitHandler}>
          <div style={{ margin: "8px" }}>
            <TextField
              id="clientKey-input"
              variant="outlined"
              label="Client Key"
              name="clientKey"
              size="small"
              value={clientkey}
              onChange={(e) => setClientkey(e.target.value)}
            />
          </div>
          <div style={{ margin: "8px" }}>
            <TextField
              id="username-input"
              variant="outlined"
              label="Username"
              name="username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div style={{ margin: "8px" }}>
            <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
              {isLoading ? "Loading..." : "Send Request"}
            </Button>
          </div>
        </form>
      </Container>
      {noReports && <p>No error reports found for the provided client key and username.</p>}
      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortedField === "report_time"}
                  direction={sortDirection}
                  onClick={() => changeSortOrder("report_time")}
                >
                  Report Time
                </TableSortLabel>
              </TableCell>
              <TableCell>UUID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedListData.map((reports, key) => (
              <TableRow data-testid={`row-${key}`} key={reports.uuid}>
                <TableCell>{reports.report_time}</TableCell>
                <TableCell>{reports.uuid}</TableCell>
                <TableCell>
                  <FormControl data-testid={`${key}-select`} size="small">
                    <Select
                      data-testid="download-select"
                      displayEmpty
                      renderValue={() => <GetApp color="primary" fontSize="small" />}
                    >
                      <MenuItem disabled value="">
                        Select Download:
                      </MenuItem>
                      {["issue-report", "gpx"].map((type) => (
                        <MenuItem key={`${type}-${reports.uuid}`}>
                          <Button
                            data-testid={type === "issue-report" ? "report-btn" : "gpx-btn"}
                            color="primary"
                            onClick={() => onDownload(reports.uuid, type)}
                            startIcon={<Download />}
                          >
                            {type === "issue-report" ? "Issue Report" : "GPX File"}
                          </Button>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
